// Bridge between the Capacitor Health Connect plugin (Android wrapper only)
// and the Regimen backend. No-ops when running in a regular browser PWA.
//
// Reads two record types from Health Connect:
//   - Weight              -> kilograms
//   - SleepSession        -> total minutes per local wake-up day
//
// And POSTs them to /api/metrics/sync-from-health-connect grouped by date.

const LAST_SYNC_KEY = 'healthConnect.lastSyncAt';
const READ_TYPES = ['Weight', 'SleepSession'];

// Maps our record type names to the Android Health Connect permission strings
// the plugin returns from checkHealthPermissions (snake_case suffix).
const TYPE_TO_PERMISSION_SUFFIX = {
  Weight: 'READ_WEIGHT',
  SleepSession: 'READ_SLEEP'
};

const filterGrantedTypes = (types = []) => READ_TYPES.filter(type => types.includes(type));

function isNative() {
  return (
    typeof window !== 'undefined' &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform()
  );
}

function getPlugin() {
  if (!isNative()) return null;
  return window.Capacitor?.Plugins?.HealthConnect || null;
}

async function getPreferences() {
  if (!isNative()) return null;
  return window.Capacitor?.Plugins?.Preferences || null;
}

async function getLastSyncAt() {
  const prefs = await getPreferences();
  if (prefs) {
    try {
      const { value } = await prefs.get({ key: LAST_SYNC_KEY });
      return value || null;
    } catch (_) { /* fall through */ }
  }
  try {
    return localStorage.getItem(LAST_SYNC_KEY);
  } catch (_) {
    return null;
  }
}

async function setLastSyncAt(iso) {
  const prefs = await getPreferences();
  if (prefs) {
    try {
      await prefs.set({ key: LAST_SYNC_KEY, value: iso });
      return;
    } catch (_) { /* fall through */ }
  }
  try {
    localStorage.setItem(LAST_SYNC_KEY, iso);
  } catch (_) { /* ignore */ }
}

async function isAvailable() {
  const plugin = getPlugin();
  if (!plugin) return { available: false, reason: 'not-native' };
  try {
    // The plugin exposes checkAvailability() returning
    // 'Available' | 'NotInstalled' | 'NotSupported'.
    const res = await plugin.checkAvailability();
    return { available: res?.availability === 'Available', status: res?.availability };
  } catch (err) {
    return { available: false, reason: err?.message || 'unknown' };
  }
}

async function getPermissionStatus() {
  const plugin = getPlugin();
  if (!plugin) return { hasAll: false, grantedTypes: [], rawGranted: [] };
  try {
    const res = await plugin.checkHealthPermissions({
      read: READ_TYPES,
      write: []
    });
    console.log('[HealthConnect] checkHealthPermissions raw:', JSON.stringify(res));
    const rawGranted = Array.from(res?.grantedPermissions || res?.readPermissions || []);
    const grantedTypes = READ_TYPES.filter((t) => {
      const suffix = TYPE_TO_PERMISSION_SUFFIX[t];
      return rawGranted.some((g) => {
        const s = String(g);
        return s === t || (suffix && (s.endsWith(suffix) || s.toUpperCase().endsWith(suffix)));
      });
    });
    // Plugin's hasAllPermissions boolean is authoritative — it's computed
    // using the exact same permission strings on the Android side. Trust it
    // for the "all granted" check; our string mapping is only used for
    // partial-grant UX display.
    const hasAll = typeof res?.hasAllPermissions === 'boolean'
      ? res.hasAllPermissions
      : grantedTypes.length === READ_TYPES.length;
    return { hasAll, grantedTypes, rawGranted };
  } catch (err) {
    console.error('[HealthConnect] checkHealthPermissions failed:', err);
    return { hasAll: false, grantedTypes: [], rawGranted: [] };
  }
}

async function getGrantedTypes() {
  const { grantedTypes } = await getPermissionStatus();
  return grantedTypes;
}

async function hasAllPermissions() {
  const { hasAll } = await getPermissionStatus();
  return hasAll;
}

async function requestPermissions() {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, granted: [], rawGranted: [], reason: 'not-native' };
  try {
    const res = await plugin.requestHealthPermissions({
      read: READ_TYPES,
      write: []
    });
    console.log('[HealthConnect] requestHealthPermissions raw:', JSON.stringify(res));
  } catch (err) {
    console.error('[HealthConnect] requestPermissions threw:', err);
  }
  // Always re-check via checkHealthPermissions — the inline payload is
  // unreliable on some devices (resolves before Settings returns control).
  const { hasAll, grantedTypes, rawGranted } = await getPermissionStatus();
  return { ok: hasAll, granted: grantedTypes, rawGranted };
}

async function readRecords(plugin, type, startISO, endISO) {
  try {
    const res = await plugin.readRecords({
      type,
      timeRangeFilter: {
        type: 'between',
        startTime: startISO,
        endTime: endISO
      }
    });
    return res?.records || [];
  } catch (err) {
    console.error(`[HealthConnect] readRecords(${type}) failed:`, err);
    return [];
  }
}

// Build a per-date map and post to backend.
async function syncRecent(backendService, { sinceISO } = {}) {
  const identity = backendService.authenticatedIdentity;
  const positionOwner = backendService.positionOwner;
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'not-native' };

  const { hasAll, grantedTypes } = await getPermissionStatus();
  // If hasAll is true but our string mapping somehow returned fewer types,
  // trust the plugin and read everything.
  const typesToRead = hasAll ? READ_TYPES : grantedTypes;
  console.log('[HealthConnect] syncRecent hasAll:', hasAll, 'grantedTypes:', grantedTypes, 'typesToRead:', typesToRead);
  if (typesToRead.length === 0) return { ok: false, reason: 'no-permission' };

  const end = new Date();
  // Health Connect only lets an app read the last 30 days of non-medical data
  // unless it holds PERMISSION_READ_HEALTH_DATA_HISTORY (which this plugin
  // can't request). Reading a window that starts before that 30-day cutoff
  // makes the native readRecords() throw a SecurityException, which our
  // per-type try/catch swallows -> every type comes back empty and NOTHING
  // syncs (this is why daily sleep stopped uploading after we widened the
  // window to 365 days). Cap the lookback safely inside the 30-day limit so
  // recent data — including last night's sleep — always reads. The backend
  // dedupes via the scoped Health Connect importer, so re-sending these days is
  // cheap. A caller can still pass a narrower { sinceISO }; we clamp it to
  // the same safe floor.
  const MAX_LOOKBACK_DAYS = 28; // stay a couple days inside HC's ~30-day cap
  const safeFloor = new Date();
  safeFloor.setUTCDate(safeFloor.getUTCDate() - MAX_LOOKBACK_DAYS);

  let start;
  if (sinceISO) {
    start = new Date(sinceISO);
  }
  if (!start || isNaN(start.getTime()) || start < safeFloor) {
    start = safeFloor;
  }

  const startISO = start.toISOString();
  const endISO = end.toISOString();

  const shouldRead = (t) => typesToRead.includes(t);
  const [weightRecords, sleepRecords] = await Promise.all([
    shouldRead('Weight') ? readRecords(plugin, 'Weight', startISO, endISO) : Promise.resolve([]),
    shouldRead('SleepSession') ? readRecords(plugin, 'SleepSession', startISO, endISO) : Promise.resolve([])
  ]);
  console.log('[HealthConnect] read counts:', {
    weight: weightRecords.length,
    sleep: sleepRecords.length
  });

  // Weight uses the local reading day; sleep sums session minutes on the local wake-up day.
  const byDate = new Map();
  const ensure = (date) => {
    if (!byDate.has(date)) byDate.set(date, { date });
    return byDate.get(date);
  };

  for (const r of weightRecords) {
    const time = new Date(r.time || r.startTime).getTime();
    if (!Number.isFinite(time)) continue;
    // 0.0.40's native Mass serializer emits grams, not kilograms.
    // Also accept older bridge payloads and Android's inKilograms shape.
    const unit = r?.weight?.unit;
    if (unit && !['gram', 'kilogram'].includes(unit)) continue;
    const rawWeight = r?.weight?.inKilograms ?? r?.weight?.value ?? r?.weight ?? r?.weightKg;
    const kg = Number(rawWeight) / (unit === 'gram' ? 1000 : 1);
    if (!Number.isFinite(kg) || kg <= 0 || kg >= 700) continue;
    const dt = new Date(time);
    const date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    const row = ensure(date);
    if (row._weightTime == null || time > row._weightTime) {
      row.weight_kg = kg;
      row._weightTime = time;
      row.weight_recorded_at = dt.toISOString();
    }
  }


  for (const r of sleepRecords) {
    // Attribute a sleep session to the LOCAL date the user woke up on.
    // Most sleep trackers do this so a session that runs 11pm Mon -> 7am Tue
    // counts as Tuesday's sleep. Health Connect returns ISO timestamps with
    // a zone offset; new Date(...).toISOString() loses the local offset, so
    // bucket by the local date of endTime via toLocaleDateString-style logic.
    const endDate = r.endTime ? new Date(r.endTime) : null;
    if (!endDate || isNaN(endDate.getTime())) continue;
    const yyyy = endDate.getFullYear();
    const mm = String(endDate.getMonth() + 1).padStart(2, '0');
    const dd = String(endDate.getDate()).padStart(2, '0');
    const date = `${yyyy}-${mm}-${dd}`;

    const s = new Date(r.startTime).getTime();
    const e = endDate.getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    const minutes = Math.round((e - s) / 60000);
    const row = ensure(date);
    row.sleep_minutes = (row.sleep_minutes || 0) + minutes;
  }

  const records = Array.from(byDate.values())
    .map(({ _weightTime, ...rest }) => rest)
    .filter((r) => r.weight_kg != null || r.sleep_minutes != null);

  // Native permission/record reads can outlive an account transition. Never
  // submit the previous account's collected readings using the new account.
  if (backendService.authenticatedIdentity !== identity || backendService.positionOwner !== positionOwner) {
    return { ok: false, reason: 'scope-changed' };
  }
  if (records.length === 0) {
    await setLastSyncAt(endISO);
    return { ok: true, processed: 0, days: 0 };
  }

  const result = await backendService.syncFromHealthConnect(records);
  if ((result?.processed > 0 || result?.activities?.length > 0)
    && backendService.authenticatedIdentity === identity && backendService.positionOwner === positionOwner) {
    window.dispatchEvent(new CustomEvent('health-connect-synced', { detail: {
      ...result, backendService, identity, positionOwner,
    } }));
  }
  await setLastSyncAt(endISO);
  return { ok: true, ...result };
}

async function openSettings() {
  const plugin = getPlugin();
  if (!plugin || typeof plugin.openHealthConnectSetting !== 'function') {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    await plugin.openHealthConnectSetting();
    return { ok: true };
  } catch (err) {
    console.error('[HealthConnect] openHealthConnectSetting failed:', err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

const HealthConnectService = {
  isNative,
  isAvailable,
  hasAllPermissions,
  getGrantedTypes,
  getPermissionStatus,
  filterGrantedTypes,
  requestPermissions,
  openSettings,
  syncRecent,
  getLastSyncAt,
  READ_TYPES
};

export default HealthConnectService;
